import { CSSInterpolation } from '@emotion/css/create-instance'
import memoize from 'lodash/memoize'
import React, {
  type ChangeEvent,
  type FocusEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import type { ReactInputProps } from '../../../types'
import { errorInDev } from '../../../util/logging'
import { type InputState, InternalInput } from '../internal/InternalInput'
import { InternalInputWrapper } from '../internal/InternalInputWrapper'
import { dateFormatNormalizer } from './dateFormatNormalizer'

type DateVariantProps = {
  /**
   * Whether the input is for a date or a month. Default to 'date' if the prop is absent.
   * `date` - A text input of date (MM/DD/YYYY).
   */
  variant?: 'date'
  /**
   * Whether and how to show a UI date picker.
   *
   * `default` - A text input with date validation.
   * `native` - An input with type 'date' to allow for native platform date control. Not applicable to month mode.
   * `calendar` - A text input with date validation and a custom calendar dropdown.
   */
  calendarPickerVariant?: 'default' | 'native' | 'calendar'
}
type MonthVariantProps = {
  /**
   * Whether the input is for a date or a month. Default to 'date' if the prop is absent.
   * `month` - A text input of month (MM/YYYY).
   */
  variant?: 'month'
  /**
   * Whether and how to show a UI date picker.
   *
   * `default` - A text input with date validation.
   * `calendar` - A text input with date validation and a custom calendar dropdown.
   */
  calendarPickerVariant?: 'default' | 'calendar'
}

type Props = {
  /**
   * Whether to disable the input or mark it as having an error.
   */
  state?: InputState
  /**
   * Set to `true` to disable automatic formatting of the date string the user enters. Note
   * that automatic formatting of date string will be turned off if you have set `calendarPickerVariant="native"`.
   */
  disableAutoFormat?: boolean
  /**
   * An error message to show below the input. Will only be shown if `state` is `"error"`.
   */
  errorMessage?: string
  /**
   * Provide `true` to visually hide the input label, while still leaving it present in the DOM
   * for screen readers. Ignored if no `label` is provided.
   */
  hideLabel?: boolean
  /**
   * A label to show above the input. If you would like to visually hide it please use
   * `hideLabel` prop to do so. If you do not provide a value here, you must provide your own
   * `<label>` element for the input for accessibility reasons.
   */
  label?: string
  /**
   * The value to show in the input. If empty, any provided `placeholder` is shown.
   * Strings should be in either `YYYY-MM-DD` or `MM/DD/YYYY` format.
   * This should be the string `value` that the `onChange` callback returns.
   */
  value: string
  /**
   * Callback called when the value in the input changes. As a convenience, the value is provided
   * separately from the change event for use cases that just need the new value. We also provide
   * the date as a Date object for use cases that require custom date string formatting.
   */
  onChange: ({
    dateValue,
    event,
    value,
  }: {
    event: ChangeEvent<HTMLInputElement>
    value: string
    dateValue: Date | null
  }) => void
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
} & (DateVariantProps | MonthVariantProps)

type DefaultDateInputProps = Omit<
  ReactInputProps,
  | keyof Props
  | 'disabled'
  | 'onChange'
  | 'css'
  | 'className'
  | 'style'
  | 'type'
  | 'placeholder'
  | 'maxLength'
>

const DEFAULT_DATE_FORMAT_REGEXP = /^\d{1,2}([/-])\d{1,2}\1\d{4}$/
const ISO_8601_DATE_FORMAT_REGEXP = /^\d{4}([/-])\d{1,2}\1\d{1,2}$/
const DEFAULT_MONTH_FORMAT_REGEXP = /^\d{1,2}([/-])\d{4}$/
const ISO_8601_MONTH_FORMAT_REGEXP = /^\d{1,2}([/-])\d{4}$/

export function isDate(date: string | null) {
  if (date === null) {
    return false
  }

  if (
    !DEFAULT_DATE_FORMAT_REGEXP.test(date) &&
    !ISO_8601_DATE_FORMAT_REGEXP.test(date)
  ) {
    return false
  }

  return !isNaN(Date.parse(date))
}

const getFirstDayOfMonth = memoize((month: string) => {
  const delimiterIndex = /[/|-]/.exec(month)?.index
  if (!delimiterIndex) return ''
  const delimiter = month[delimiterIndex]
  const monthArr = month.split(delimiter)
  return [monthArr[0], '1', monthArr[1]].join(delimiter)
})

export function isMonth(month: string | null) {
  if (month === null) {
    return false
  }

  if (
    !DEFAULT_MONTH_FORMAT_REGEXP.test(month) &&
    !ISO_8601_MONTH_FORMAT_REGEXP.test(month)
  ) {
    return false
  }

  // NOTE(tianbi.jiang) add date to month to make "10/2021" to "10/1/2021" for date parse validation
  const firstDayInMonth = getFirstDayOfMonth(month)
  return !isNaN(Date.parse(firstDayInMonth))
}

function formatDateForChangeEvent(validDateString: string) {
  // ensure that ISO format are converted to MM/DD/YYYY
  if (ISO_8601_DATE_FORMAT_REGEXP.test(validDateString)) {
    const parts = validDateString.split(/\D/)

    return `${parts[1]}/${parts[2]}/${parts[0]}`
  }

  return validDateString
}

/**
 * An input element for collecting date values. It only accepts numeric characters and related
 * punctuation.
 *
 * Like all inputs, this should be used inside a `<form>` element. You also may want to use
 * the `getInputState` helper.
 *
 * Note that we have not yet implemented support for a calendar-style date picker. If you have
 * a specific need for a `DateInput` that uses a calendar widget, please reach out to us in
 * [#bento-design-system-discussion](https://hood.slack.com/archives/CRADQRAE7) to chat with
 * us about it.
 */
export function DateInput({
  calendarPickerVariant = 'default',
  disableAutoFormat = false,
  errorMessage,
  hideLabel = false,
  label,
  onBlur: onBlurProp,
  onChange: onChangeProp,
  overrideRootStyles,
  state = 'default',
  value,
  variant = 'date',
  ...inputProps
}: Props & DefaultDateInputProps) {
  const isAutoFormatEnabled =
    !disableAutoFormat && calendarPickerVariant !== 'native'

  if (calendarPickerVariant === 'calendar') {
    errorInDev('Calendar currently not supported')
  }

  const isMonthMode = variant === 'month'

  const inputRef = useRef<HTMLInputElement>(null)
  const previousSelectionStart = useRef<number | null>(0)
  const [displayValue, setDisplayValue] = useState(value)

  const onChange = ({
    event,
    value: newValue,
  }: {
    value: string
    event: ChangeEvent<HTMLInputElement>
  }) => {
    // filter out any non-number strings, only allow for digit, '/', and '-'
    let newValueToUse = newValue.replace(/[^\d/\-/]/g, '')

    if (isAutoFormatEnabled) {
      // if we're editing the middle, maintain cursor position
      if ((inputRef.current?.selectionStart || 0) < newValue.length - 1) {
        previousSelectionStart.current =
          typeof inputRef.current?.selectionStart === 'number'
            ? inputRef.current?.selectionStart
            : 0
      } else {
        previousSelectionStart.current = null
      }

      const formattedValue = dateFormatNormalizer({
        isMonthMode,
        previousVal: displayValue,
        val: newValue,
      })
      newValueToUse = formattedValue
    }

    setDisplayValue(newValueToUse)

    const onChangeValue =
      calendarPickerVariant === 'native'
        ? newValueToUse
        : formatDateForChangeEvent(newValueToUse)

    let dateValue
    if (isMonthMode) {
      dateValue = isMonth(onChangeValue)
        ? new Date(getFirstDayOfMonth(onChangeValue))
        : null
    } else {
      dateValue = isDate(onChangeValue) ? new Date(onChangeValue) : null
    }
    onChangeProp({
      dateValue,
      event,
      value: onChangeValue,
    })
  }

  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    // NOTE(auster.chen): when we copy paste the same date the formatting is gone due to change event not being fired so we
    // format on blur
    if (isAutoFormatEnabled) {
      const formattedValue = dateFormatNormalizer({
        isMonthMode,
        previousVal: displayValue,
        val: displayValue,
      })

      if (formattedValue !== displayValue) {
        setDisplayValue(formattedValue)
      }
    }

    onBlurProp?.(e)
  }

  useLayoutEffect(() => {
    if (isAutoFormatEnabled && previousSelectionStart.current !== null) {
      inputRef.current?.setSelectionRange(
        previousSelectionStart.current,
        previousSelectionStart.current
      )
    }
  }, [isAutoFormatEnabled, displayValue])

  useEffect(() => {
    if (value !== displayValue) {
      setDisplayValue(
        // Note (@auster.chen): Auto-formatting excludes native variant which uses ISO date format.
        // For native or non auto formatted we don't do any special formatting and just directly use the value.
        // Later we can resolve this to a standard date object so format won't matter
        isAutoFormatEnabled
          ? dateFormatNormalizer({ isMonthMode, val: value })
          : value
      )
    }
  }, [
    calendarPickerVariant,
    displayValue,
    isAutoFormatEnabled,
    isMonthMode,
    value,
  ])

  return (
    <InternalInputWrapper
      errorMessage={errorMessage}
      hideLabel={hideLabel}
      label={label}
      overrideRootStyles={overrideRootStyles}
      state={state}
    >
      {errorMessageId => (
        <InternalInput
          aria-describedby={errorMessageId}
          innerRef={inputRef}
          // Corresponding to MM/DD/YYYY length of 10, or MM/YYYY for length of 7
          maxLength={isMonthMode ? 7 : 10}
          onBlur={onBlur}
          onChange={onChange}
          placeholder={isMonthMode ? 'MM/YYYY' : 'MM/DD/YYYY'}
          state={state}
          /**
           * When type date is used the format defined by RFC 3339 https://tools.ietf.org/html/rfc3339
           * see section 5.6. Internet Date/Time Format
           * YYYY-MM-DD
           */
          type={calendarPickerVariant === 'native' ? 'date' : 'text'}
          value={displayValue}
          {...inputProps}
        />
      )}
    </InternalInputWrapper>
  )
}
